import AssetSelectionRing from './AssetSelectionRing';

/**
 * SelectableImageCard - Clickable image thumbnail for selection grids
 * 
 * Handles: selection border, hover highlight, full-bleed image, optional overlay content.
 * All hover/selected states via CSS — no JS hover handlers.
 * The image loads lazily: picker grids render one card per gallery item, and the card's
 * aspect-ratio box sizes offscreen images before they load.
 * 
 * @param {string} src - Image source URL
 * @param {string} alt - Image alt text
 * @param {string} ariaLabel - Optional button label when overlay status should be announced
 * @param {boolean} selected - Sets aria-pressed and draws the shared AssetSelectionRing
 * @param {function} onClick - Click handler
 * @param {boolean} disabled
 * @param {ReactNode} children - Optional overlay content (checkmark badge, remove button)
 * @param {string} className - Additional CSS classes
 * @param {object} style - Additional inline styles
 */
const SelectableImageCard = ({
  src,
  alt = '',
  ariaLabel = null,
  selected = false,
  onClick,
  disabled = false,
  children,
  className = '',
  draggable = false,
  onDragStart,
  onDragEnd,
  onPointerDown,
  style = {}
}) => {
  return (
    <button
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onPointerDown={onPointerDown}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel || undefined}
      aria-pressed={selected}
      data-selected={selected ? 'true' : undefined}
      className={`ui-selectable-image-card ${className}`}
      style={style}
    >
      <img
        draggable={draggable || onPointerDown ? false : undefined}
        // Before src: React sets attributes in this order, and a src already in Chrome's
        // memory cache (every image deferred on an earlier open) loads before a later
        // `loading` is seen.
        loading="lazy"
        src={src}
        alt={alt}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
        }}
      />
      {children}
      <AssetSelectionRing />
    </button>
  );
};

export default SelectableImageCard;
